// Réglage « fond de carte hors connexion » : télécharge dans le cache les tuiles des emprises des
// missions du jour, des tournées et des ordres de mission (zooms 13 à 16, 240 tuiles au plus),
// affiche l'état du cache et permet de le vider.
import { Bouton, FOND_OPENFREEMAP } from '@cc/ui';
import { Map as IconeCarte, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TUILES_MAX, cacheDisponible, empriseAutour, etatCacheTuiles, gabaritTuiles, mettreEnCache, tuilesPourEmprise, urlsGlyphes, urlsTuiles, viderCacheTuiles, type AvancementCache } from '../../terrain/carte-cache';
import { tailleLisible } from '../../terrain/photos';
import { useEnLigne, useTravailLocal } from '../../terrain/useTerrain';
import { Definitions, Section } from './commun';
import styles from './terrain.module.css';

export function SectionCarteHorsConnexion() {
  const travail = useTravailLocal();
  const enLigne = useEnLigne();
  const [etat, setEtat] = useState<{ entrees: number; octets: number } | null>(null);
  const [avancement, setAvancement] = useState<AvancementCache | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tour, setTour] = useState(0);

  useEffect(() => {
    let actif = true;
    void etatCacheTuiles().then((e) => actif && setEtat(e));
    return () => {
      actif = false;
    };
  }, [tour]);

  const positions = [
    ...(travail.donnees?.missions ?? []).map((m) => m.intervention.position),
    ...(travail.donnees?.tournees ?? []).flatMap((t) => t.points.map((p) => p.position)),
    ...(travail.donnees?.ordres_mission ?? []).map((o) => o.position),
  ].filter((p): p is NonNullable<typeof p> => p !== null);

  const telecharger = async () => {
    setMessage(null);
    const emprise = empriseAutour(positions.map((p) => [p.longitude, p.latitude]));
    if (!emprise) {
      setMessage('Aucune position connue dans le travail téléchargé.');
      return;
    }
    const gabarit = await gabaritTuiles(FOND_OPENFREEMAP);
    if (!gabarit) {
      setMessage('Le serveur de tuiles est injoignable pour le moment.');
      return;
    }
    const tuiles = tuilesPourEmprise(emprise);
    const urls = [...urlsGlyphes(FOND_OPENFREEMAP), FOND_OPENFREEMAP.tuiles, ...urlsTuiles(gabarit, tuiles)];
    setAvancement({ total: urls.length, faites: 0, deja: 0, echecs: 0, octets: 0, terminee: false });
    const a = await mettreEnCache(urls, setAvancement);
    setAvancement(null);
    setMessage(`${a.faites} tuile(s) téléchargée(s), ${a.deja} déjà en cache, ${a.echecs} échec(s), ${tailleLisible(a.octets)} ajoutés${tuiles.length >= TUILES_MAX ? ` (limite de ${TUILES_MAX} tuiles atteinte : zone réduite aux zooms les plus larges)` : ''}.`);
    setTour((t) => t + 1);
  };

  const vider = async () => {
    await viderCacheTuiles();
    setMessage('Cache des tuiles vidé.');
    setTour((t) => t + 1);
  };

  if (!cacheDisponible()) {
    return (
      <Section titre="Carte hors connexion">
        <p className={styles.aide}>Le cache de tuiles n&apos;est pas disponible sur cet appareil (navigation privée ou navigateur ancien) : la carte exige le réseau.</p>
      </Section>
    );
  }
  return (
    <Section titre="Carte hors connexion">
      <Definitions elements={[{ terme: 'Tuiles en cache', valeur: etat ? `${etat.entrees} (${tailleLisible(etat.octets)})` : 'lecture en cours' }, { terme: 'Positions connues dans le travail', valeur: String(positions.length) }]} />
      <div className={styles.ligneBoutons}>
        <Bouton variante="secondaire" icone={IconeCarte} onClick={() => void telecharger()} chargement={avancement !== null} libelleChargement={avancement ? `Téléchargement ${avancement.faites + avancement.deja + avancement.echecs} sur ${avancement.total}` : 'Téléchargement en cours'} disabled={!enLigne || positions.length === 0}>
          Télécharger le fond de carte des missions du jour
        </Bouton>
        <Bouton variante="discret" icone={Trash2} onClick={() => void vider()} disabled={!etat || etat.entrees === 0}>
          Vider le cache des tuiles
        </Bouton>
      </div>
      {!enLigne ? <p className={styles.aide}>Hors connexion : le téléchargement des tuiles attend le réseau ; les tuiles déjà en cache restent affichées.</p> : null}
      {message ? (
        <p role="status" className={styles.aide}>
          {message}
        </p>
      ) : null}
    </Section>
  );
}
